// information on the error handling structure can be found here: https://calix.atlassian.net/wiki/spaces/ECOM/pages/5161255324/Quote+Manager+Clone+Route
import { Container } from '@Calix-Commerce/design-system';
import {
  loadingState,
  currentQuoteValidationState,
  selectedAccountState,
  authenticationState,
  currentQuoteState,
} from 'state/atoms';
import { useParams } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { useCallback, useEffect, useState } from 'react';
import { useAppNavigation } from 'utils/hooks';
import { useQuoteActionController } from 'utils/hooks';
import { storageCurrentQuoteSelector } from 'state/selectors';
import { ErrorMessage } from 'types/Error';
import { UnsavedChangesModal } from 'components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { VAR } from 'connectors/userTypes';
import { SingleActionModal } from 'components/modals/SingleActionModal';
import { FETCH_POLICY } from 'utils/constants/general';

const QuoteClone = () => {
  const { transactionId } = useParams();
  const { accountId } = useRecoilValue(selectedAccountState);
  const [currentQuote, setCurrentQuote] = useRecoilState(storageCurrentQuoteSelector);
  const isNewQuote = Boolean(currentQuote.transactionId) === false;
  const validationState = useRecoilValue(currentQuoteValidationState);
  const { userType } = useRecoilValue(authenticationState);
  const [runQuoteClone, setRunQuoteClone] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showAccountRedirectModal, setShowAccountRedirectModal] = useState(false);
  const [showQuoteDetailsRedirectModal, setShowQuoteDetailsRedirectModal] = useState(false);
  const [showQuoteListRedirectModal, setShowQuoteListRedirectModal] = useState(false);
  const {
    redirectToQuoteDetails,
    redirectToQuoteDetailsForNew,
    redirectToAccountSearch,
    redirectToQuoteList,
  } = useAppNavigation();
  const setGlobalLoader = useSetRecoilState(loadingState);
  const { loadQuote, alterQuote, createQuote, cloneQuote } = useQuoteActionController();

  const onSaveUnsavedChanges = () => {
    setGlobalLoader(true);
    setShowUnsavedModal(false);

    if (isNewQuote) {
      createQuote({
        quoteData: { ...currentQuote, accountId },
        validationData: validationState,
        redirectToNewQuote: false,
      })
        .then(() => {
          return loadQuote({
            transactionId: transactionId as string,
            overwriteLocalQuote: true,
            fetchPolicy: FETCH_POLICY.NO_CACHE,
          });
        })
        .then((response) => {
          setCurrentQuote(response);
          setRunQuoteClone(true);
        })
        .catch((error) => {
          finalModalErrorHandling(error);
        });
    } else {
      alterQuote({ quoteData: currentQuote, validationData: validationState })
        .then(() => {
          return loadQuote({
            transactionId: transactionId as string,
            overwriteLocalQuote: true,
            fetchPolicy: FETCH_POLICY.NO_CACHE,
          });
        })
        .then((response) => {
          setCurrentQuote(response);
          setRunQuoteClone(true);
        })
        .catch((error) => {
          finalModalErrorHandling(error);
        });
    }
  };

  const discardUnsavedChangesModal = () => {
    setGlobalLoader(true);
    setShowUnsavedModal(false);
    loadQuote({
      transactionId: transactionId as string,
      overwriteLocalQuote: true,
      fetchPolicy: FETCH_POLICY.NO_CACHE,
    })
      .then(() => {
        setRunQuoteClone(true);
      })
      .catch((error) => {
        finalModalErrorHandling(error);
      });
  };

  const cancelUnsavedChangesModal = () => {
    setShowUnsavedModal(false);
    if (transactionId) {
      redirectToQuoteDetails(transactionId, true);
    } else {
      redirectToQuoteDetailsForNew(false);
    }
  };

  const initialErrorHandling = (error) => {
    try {
      setGlobalLoader(false);

      // if there is an unsaved, non-var quote, allow the user clone options. Otherwise, exit without cloning
      if (error.message === ErrorMessage.MODIFIED_QUOTE && userType !== VAR) {
        setShowUnsavedModal(true);
      } else {
        finalModalErrorHandling(undefined);
      }
    } catch (error) {
      finalModalErrorHandling(error);
    }
  };

  const finalModalErrorHandling = (error: unknown | undefined) => {
    setGlobalLoader(false);

    if (error) {
      console.error('Unhandlable error encountered, redirecting away from clone page: ', error);
    }

    if (userType === VAR) {
      setShowAccountRedirectModal(true);
    } else if (currentQuoteState) {
      setShowQuoteDetailsRedirectModal(true);
    } else {
      setShowQuoteListRedirectModal(true);
    }
  };

  const cloneQuoteAndRedirect = useCallback(() => {
    return cloneQuote({
      quoteData: Object.assign({}, currentQuote, { transactionId: transactionId }),
      validationData: validationState,
    }).then((response) => {
      setGlobalLoader(false);
      redirectToQuoteDetails(response.transactionId, true);
    });
  }, [
    currentQuote,
    validationState,
    cloneQuote,
    setGlobalLoader,
    redirectToQuoteDetails,
    transactionId,
  ]);

  useEffect(() => {
    if (runQuoteClone) {
      setRunQuoteClone(false);
      cloneQuoteAndRedirect()
        .catch((error) => {
          initialErrorHandling(error);
        })
        .finally(() => {
          setGlobalLoader(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationState, runQuoteClone, currentQuote]);

  useEffect(() => {
    setGlobalLoader(true);
    setRunQuoteClone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onCancel={cancelUnsavedChangesModal}
        onConfirmation={onSaveUnsavedChanges}
        onDiscard={discardUnsavedChangesModal}
        modalContent={
          <p>
            You're leaving{' '}
            {currentQuote.transactionId ? (
              <span>
                quote <b>{`${currentQuote.transactionId}`}</b> with unsaved changes
              </span>
            ) : (
              <span>{'an unsaved quote'}</span>
            )}{' '}
            to clone <b>{transactionId as string}</b>
          </p>
        }
      />

      <SingleActionModal
        title={'Error Cloning'}
        isOpen={showQuoteListRedirectModal}
        content={
          'An error was encountered while attempting to clone your quote. Click okay to navigate back to the quote list page.'
        }
        confirmButtonText={'OK'}
        onConfirmation={() => redirectToQuoteList(true)}
      />

      <SingleActionModal
        title={'Error Cloning'}
        isOpen={showQuoteDetailsRedirectModal}
        content={
          'An error was encountered while attempting to clone your quote. Click okay to navigate back to the quote details page.'
        }
        confirmButtonText={'OK'}
        onConfirmation={() => redirectToQuoteDetails(transactionId, true)}
      />

      <SingleActionModal
        title={'Error Cloning'}
        isOpen={showAccountRedirectModal}
        content={
          'An error was encountered while attempting to clone your quote. Click okay to navigate back to the account search page.'
        }
        confirmButtonText={'OK'}
        onConfirmation={() => redirectToAccountSearch(true)}
      />
    </Container>
  );
};

export { QuoteClone };
